// Booking Lead Time Distribution — FIRST CUT.
//
// GOAL (full version): histogram of (appointmentDate − session.createdAt),
// bucketed same-day / 1-7d / 8-30d / >30d, per bot. Answers "how far out
// are callers scheduling?" for the Ortho LOB.
//
// LIMITATION: the actual appointmentDate lives in the ScheduleAppointment
// tool-call payload inside per-message metadata (requires a per-session
// get-ai-agent-session-messages fan-out, same pattern BotToolAudit uses).
// For a first cut we approximate: rows = (bot × day), showing bookings-
// completed (lastAction matches /schedule|book|appointment/i AND outcome
// HANDLED), bookings-failed, and bookings-escalated. Supervisors still
// get an "automated scheduling throughput" view; true lead-time histogram
// comes when we wire the message-level fan-out.
//
// Pure-client aggregation — reuses the existing fetched session window.

import React, { useMemo, useState } from 'react';
import { Badge, Group, Text, Tooltip } from '@mantine/core';
import { ReportTable } from './ReportTable';
import { SessionListModal } from '../components/SessionListModal';

const REPORT_ID = 'booking-lead-time';

// Any session whose lastAction string contains these tokens is treated as
// a booking-attempt session. If a tenant uses different naming, extend
// here. Case-insensitive.
const BOOKING_ACTION_PATTERN = /schedule|book|appointment|reschedule/i;

const COLUMNS = [
  {
    id: 'day',
    label: 'Day',
    pinned: true,
    render: (r) => <Text size="sm" fw={500}>{r.day}</Text>,
  },
  {
    id: 'bot',
    label: 'Bot',
    pinned: true,
    render: (r) => <Text size="sm">{r.bot || <em>(unnamed)</em>}</Text>,
  },
  {
    id: 'bookingAttempts',
    label: 'Attempts',
    hint: 'Sessions where lastAction matches schedule/book/appointment/reschedule.',
    align: 'right',
    render: (r) => <Text size="sm" fw={600}>{r.attempts}</Text>,
  },
  {
    id: 'completed',
    label: 'Completed',
    hint: 'Attempt + outcome HANDLED/COMPLETED.',
    align: 'right',
    render: (r) => <Badge size="sm" color="teal" variant="light">{r.completed}</Badge>,
  },
  {
    id: 'escalated',
    label: 'Escalated',
    hint: 'Attempt + outcome ESCALATED (caller sent to a human).',
    align: 'right',
    render: (r) => <Badge size="sm" color="blue" variant="light">{r.escalated}</Badge>,
  },
  {
    id: 'abandoned',
    label: 'Abandoned',
    hint: 'Attempt + outcome ABANDONED.',
    align: 'right',
    render: (r) => <Badge size="sm" color="orange" variant="light">{r.abandoned}</Badge>,
  },
  {
    id: 'failed',
    label: 'Failed',
    align: 'right',
    render: (r) => <Badge size="sm" color="red" variant="light">{r.failed}</Badge>,
  },
  {
    id: 'completionRate',
    label: 'Completion rate',
    hint: 'Completed / attempts.',
    align: 'right',
    render: (r) => (
      <Tooltip label={`${r.completed} of ${r.attempts} booking attempts completed`}>
        <Badge
          size="sm"
          color={r.completionRate >= 70 ? 'teal' : r.completionRate >= 40 ? 'yellow' : 'red'}
          variant={r.completionRate >= 20 ? 'filled' : 'light'}
        >
          {r.completionRate}%
        </Badge>
      </Tooltip>
    ),
  },
  {
    id: 'avgDuration',
    label: 'Avg attempt duration',
    hint: 'Mean duration of booking-attempt sessions on this day. Longer = caller struggle.',
    align: 'right',
    render: (r) => <Text size="sm">{r.avgDurationMs != null ? `${(r.avgDurationMs / 1000).toFixed(0)}s` : '—'}</Text>,
  },
  {
    id: 'avgMessages',
    label: 'Avg messages',
    hint: 'Mean messages per booking-attempt session. Spikes = conversational friction.',
    align: 'right',
    render: (r) => <Text size="sm">{r.avgMessages?.toFixed(1) ?? '—'}</Text>,
  },
];

const DEFAULT_VISIBLE = ['bookingAttempts', 'completed', 'escalated', 'abandoned', 'failed', 'completionRate', 'avgDuration', 'avgMessages'];

export function BookingLeadTime({ data, onViewSession, window }) {
  const sessions = data?.sessions ?? [];
  const rows = useMemo(() => buildRows(sessions), [sessions]);
  const bookingSessionsByKey = useMemo(() => groupBookingSessionsByBotDay(sessions), [sessions]);
  const totalAttempts = rows.reduce((n, r) => n + r.attempts, 0);
  const totalCompleted = rows.reduce((n, r) => n + r.completed, 0);

  const [drill, setDrill] = useState(null); // { bot, day }
  const csvRows = useMemo(() => rows.map(toCsvRow), [rows]);
  const csvFilename = `booking-activity-${new Date().toISOString().slice(0, 10)}.csv`;

  return (
    <>
      <ReportTable
        title="Booking activity (first cut — lastAction heuristic)"
        subtitle={
          `${totalCompleted}/${totalAttempts} booking attempt${totalAttempts === 1 ? '' : 's'} completed · ` +
          `bot × day · identified by lastAction matching /schedule|book|appointment|reschedule/i. ` +
          `True lead-time histogram (appointmentDate − sessionStart) requires per-session message fan-out — follow-up build.` +
          (onViewSession ? ' Click a row for the booking-attempt sessions on that bot/day.' : '')
        }
        reportId={REPORT_ID}
        columns={COLUMNS}
        defaultVisibleIds={DEFAULT_VISIBLE}
        rows={rows}
        emptyLabel="No booking-attempt sessions matched in this window."
        onRowClick={onViewSession ? (r) => setDrill({ bot: r.bot, day: r.day }) : undefined}
        csvExport={{ filename: csvFilename, rows: csvRows }}
      />
      <SessionListModal
        opened={!!drill}
        onClose={() => setDrill(null)}
        title={drill ? `${drill.bot} · ${drill.day}` : ''}
        subtitle={drill ? `Booking-attempt sessions on this bot-day` : ''}
        sessions={drill ? (bookingSessionsByKey.get(`${drill.day}|${drill.bot}`) ?? []) : []}
        onViewSession={onViewSession}
        window={window}
      />
    </>
  );
}

function groupBookingSessionsByBotDay(sessions) {
  const m = new Map();
  for (const s of sessions ?? []) {
    const action = s?.lastAction ?? '';
    if (!BOOKING_ACTION_PATTERN.test(String(action))) continue;
    const name = s?.agentName ?? '(unnamed)';
    const day = isoDay(s?.sessionStartTime);
    if (!day) continue;
    const key = `${day}|${name}`;
    if (!m.has(key)) m.set(key, []);
    m.get(key).push(s);
  }
  return m;
}

function toCsvRow(r) {
  return {
    day: r.day,
    bot: r.bot,
    attempts: r.attempts,
    completed: r.completed,
    escalated: r.escalated,
    abandoned: r.abandoned,
    failed: r.failed,
    completion_rate_pct: r.completionRate,
    avg_duration_ms: r.avgDurationMs ?? '',
    avg_messages: r.avgMessages != null ? r.avgMessages.toFixed(1) : '',
  };
}

function buildRows(sessions) {
  const buckets = new Map(); // key = `${day}|${bot}`
  for (const s of sessions) {
    const action = s.lastAction ?? '';
    if (!BOOKING_ACTION_PATTERN.test(String(action))) continue;

    const bot = s.agentName ?? '(unnamed)';
    const day = isoDay(s.sessionStartTime);
    if (!day) continue;
    const key = `${day}|${bot}`;

    if (!buckets.has(key)) {
      buckets.set(key, {
        key, day, bot,
        attempts: 0,
        completed: 0, escalated: 0, abandoned: 0, failed: 0, other: 0,
        sumDur: 0, countDur: 0,
        sumMsgs: 0, countMsgs: 0,
      });
    }
    const b = buckets.get(key);
    b.attempts += 1;
    switch (s.interactionOutcome) {
      case 'HANDLED': case 'COMPLETED': b.completed += 1; break;
      case 'ESCALATED': b.escalated += 1; break;
      case 'ABANDONED': b.abandoned += 1; break;
      case 'FAILED': b.failed += 1; break;
      default: b.other += 1;
    }
    if (s.duration != null) { b.sumDur += s.duration; b.countDur += 1; }
    if (s.noOfTotalMessages != null) { b.sumMsgs += s.noOfTotalMessages; b.countMsgs += 1; }
  }

  return [...buckets.values()]
    .map((b) => ({
      key: b.key, day: b.day, bot: b.bot,
      attempts: b.attempts,
      completed: b.completed,
      escalated: b.escalated,
      abandoned: b.abandoned,
      failed: b.failed,
      completionRate: pct(b.completed, b.attempts),
      avgDurationMs: safeAvgInt(b.sumDur, b.countDur),
      avgMessages: safeAvgFloat(b.sumMsgs, b.countMsgs),
    }))
    .sort((a, b) => (a.day === b.day ? b.attempts - a.attempts : b.day.localeCompare(a.day)));
}

function isoDay(ts) {
  if (ts == null) return null;
  const n = typeof ts === 'number' ? ts : Number(ts);
  const d = !isNaN(n) && n > 1e12 ? new Date(n) : new Date(ts);
  if (isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}
function safeAvgFloat(sum, count) { return count > 0 ? sum / count : null; }
function safeAvgInt(sum, count) { return count > 0 ? Math.round(sum / count) : null; }
function pct(n, total) { return total > 0 ? Math.round((n / total) * 100) : 0; }
